import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AirVent, ArrowUpDown, BadgePercent, Building2, CalendarCheck, Car, Check, CookingPot, Fence, Flower2, Hammer, Home, Laptop,
  PawPrint, PlugZap, ShieldCheck, Sofa, Sparkles, SquareParking, Sun, TrainFront, TrendingDown, Wifi,
} from 'lucide-react';
import { DymekNajazdu } from '@/components/ui/dymek-najazdu';
import { cn } from '@/lib/utils';
import { formatLiczby } from '@/lib/i18nNumber';
import { KOLEJNOSC_WSTAZKI_KARTY, pastylkaMapy, pastylkiKarty, rokRemontu, type IdWyroznika, type SzczegolRemontuWlasciciela, type Wyroznik } from '@/lib/wyroznikiOferty';

/**
 * Trzy powierzchnie wyróżników z jednego źródła (`src/lib/wyroznikiOferty.ts`, tam reguły i testy):
 * wstążka na zdjęciu, pastylki na karcie w wynikach, sekcja „Co wyróżnia to mieszkanie” na ofercie.
 * Ten plik tylko rysuje.
 */

const IKONA: Partial<Record<IdWyroznika, ReactNode>> = {
  zweryfikowany: <ShieldCheck className="h-4 w-4 text-success" />,
  wlasciciel: <Home className="h-4 w-4 text-muted-foreground" />,
  zwierzeta: <PawPrint className="h-4 w-4" />,
  tanio: <TrendingDown className="h-4 w-4" />,
  obnizka: <BadgePercent className="h-4 w-4" />,
  media_w_cenie: <PlugZap className="h-4 w-4" />,
  garaz_w_cenie: <Car className="h-4 w-4" />,
  nowe: <Sparkles className="h-4 w-4" />,
  remont_wlasciciela: <Hammer className="h-4 w-4" />,
  remont: <Hammer className="h-4 w-4" />,
};

/** Ikony małych pastylek na ofercie — każdy wyróżnik ma swoją (pastylka to ikona + krótka etykieta). */
const IKONA_PASTYLKI: Record<IdWyroznika, ReactNode> = {
  zweryfikowany: <ShieldCheck className="h-3.5 w-3.5 text-success" />,
  wlasciciel: <Home className="h-3.5 w-3.5" />,
  zwierzeta: <PawPrint className="h-3.5 w-3.5" />,
  tanio: <TrendingDown className="h-3.5 w-3.5" />,
  obnizka: <BadgePercent className="h-3.5 w-3.5" />,
  media_w_cenie: <PlugZap className="h-3.5 w-3.5" />,
  garaz_w_cenie: <Car className="h-3.5 w-3.5" />,
  parking_w_cenie: <SquareParking className="h-3.5 w-3.5" />,
  nowe: <Sparkles className="h-3.5 w-3.5" />,
  taras: <Sun className="h-3.5 w-3.5" />,
  ogrod: <Flower2 className="h-3.5 w-3.5" />,
  garaz: <Car className="h-3.5 w-3.5" />,
  klima: <AirVent className="h-3.5 w-3.5" />,
  remont_wlasciciela: <Hammer className="h-3.5 w-3.5" />,
  remont: <Hammer className="h-3.5 w-3.5" />,
  nowe_budownictwo: <Building2 className="h-3.5 w-3.5" />,
  swiatlowod: <Wifi className="h-3.5 w-3.5" />,
  biurko: <Laptop className="h-3.5 w-3.5" />,
  od_zaraz: <CalendarCheck className="h-3.5 w-3.5" />,
  zmywarka: <CookingPot className="h-3.5 w-3.5" />,
  winda: <ArrowUpDown className="h-3.5 w-3.5" />,
  metro: <TrainFront className="h-3.5 w-3.5" />,
  parking: <SquareParking className="h-3.5 w-3.5" />,
  balkon: <Fence className="h-3.5 w-3.5" />,
  umeblowane: <Sofa className="h-3.5 w-3.5" />,
};

/** Kolor wstążki z tokenów (Konwencja 19): zwierzęta jak dotychczasowa plakietka, taniej — info. */
const TLO_WSTAZKI: Partial<Record<IdWyroznika, string>> = {
  zwierzeta: 'bg-success text-success-foreground',
  tanio: 'bg-info text-info-foreground',
  obnizka: 'bg-info text-info-foreground',
};

/** (1) Wstążka przy lewej krawędzi zdjęcia: najwyżej jedna, karta i oferta (`wyroznikWstazki`). */
export function WstazkaWyroznika({ w, duza = false, className }: { w: Wyroznik | null; duza?: boolean; className?: string }) {
  const { t } = useTranslation();
  if (!w) return null;
  return (
    <span
      data-wstazka={w.id}
      className={cn(
        'pointer-events-none absolute left-0 z-20 inline-flex items-center gap-1.5 rounded-r-full py-1 pl-3 pr-3.5 font-semibold shadow-md ring-1 ring-black/5',
        duza ? 'top-4 text-sm' : 'top-3 text-xs',
        TLO_WSTAZKI[w.id] ?? 'bg-background text-foreground',
        className,
      )}
    >
      {IKONA[w.id]}
      {t(`offer.wyrozniki.etykieta.${w.id}`)}
    </span>
  );
}

/**
 * Ile pastylek zmieści się CAŁYCH w `dostepna` px, licząc miejsce na „+N” za nimi, gdy coś zostaje.
 * Bez wielokropka (Piotr 7.10): gdy nawet pierwsza się nie mieści, zero, a wszystkie idą do „+N”.
 * Szerokości 0 (jsdom, pierwsza klatka przed pomiarem) = wszystkie się mieszczą.
 */
export function ileSieMiesci(szerokosci: readonly number[], lacznie: number, plus: number, dostepna: number, odstep: number): number {
  for (let k = szerokosci.length; k >= 1; k--) {
    const pastylki = szerokosci.slice(0, k).reduce((a, b) => a + b, 0) + odstep * (k - 1);
    const zPlusem = pastylki + (lacznie > k ? odstep + plus : 0);
    if (zPlusem <= dostepna) return k;
  }
  return 0;
}

const KLASA_PASTYLKI = 'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium';
/**
 * „+N”: stała szerokość (`min-w`, wyśrodkowany tekst), więc „+2” i „+12” nie przesuwają rzędu.
 * Pole dotyku 44×44 px (`after:`) wychodzi poza pastylkę, która ma ok. 20 px wysokości.
 */
const KLASA_PLUSA =
  'relative inline-flex min-w-[2.25rem] shrink-0 cursor-help items-center justify-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground hover:bg-muted/70 ' +
  "after:absolute after:left-1/2 after:top-1/2 after:h-11 after:w-11 after:-translate-x-1/2 after:-translate-y-1/2 after:content-['']";

/**
 * „+N” z dymkiem: wymienia wyróżniki, które nie zmieściły się na karcie. Mysz: najechanie;
 * klawiatura: Enter; telefon: dotknięcie (Piotr 7.10 wybrał dymek, nie przejście do oferty).
 * Klik ani dotknięcie nie otwierają oferty; przewinięcie listy zamyka dymek.
 */
export function PlusWyroznikow({ schowane, className }: { schowane: readonly Wyroznik[]; className?: string }) {
  const { t } = useTranslation();
  if (!schowane.length) return null;
  const etykiety = schowane.map((w) => t(`offer.wyrozniki.etykieta.${w.id}`));
  return (
    <DymekNajazdu
      wewnatrzLinku
      side="top"
      zamknijPrzyPrzewijaniu
      className="w-auto max-w-[min(16rem,calc(100vw-2rem))] p-3 text-sm"
      data-dymek-plusa=""
      wyzwalacz={
        <button
          type="button"
          data-plus-wyroznikow=""
          aria-label={t('offer.wyrozniki.wiecej_aria', { lista: etykiety.join(', ') })}
          className={cn(KLASA_PLUSA, 'outline-none focus-visible:ring-2 focus-visible:ring-primary', className)}
        >
          {t('offer.wyrozniki.wiecej', { count: schowane.length })}
        </button>
      }
    >
      <ul className="space-y-1.5">
        {schowane.map((w, i) => (
          <li key={w.id} className="flex items-center gap-2 whitespace-nowrap text-foreground">
            {w.pasuje ? <Check className="h-3.5 w-3.5 shrink-0 text-success" aria-hidden /> : IKONA_PASTYLKI[w.id]}
            {etykiety[i]}
          </li>
        ))}
      </ul>
    </DymekNajazdu>
  );
}

/**
 * (2) Karta w wynikach: najwyżej 2 pastylki + „+N”; cecha z filtra pierwsza, z ✓. Ile z tych dwóch
 * faktycznie stanie, decyduje POMIAR szerokości karty (Piotr 30.09, zrzut: w siatce trzech kolumn
 * „Przyjazne zwie…”, „Nowe budow…”) — to, co nie mieści się całe, idzie do „+N”.
 */
export function PastylkiWyroznikow({ lista, naWstazce }: { lista: readonly Wyroznik[]; naWstazce: Wyroznik | null }) {
  const { t } = useTranslation();
  const { widoczne, ukryte, schowane } = pastylkiKarty(lista, naWstazce);
  const lacznie = widoczne.length + ukryte;
  const rzad = useRef<HTMLDivElement>(null);
  const miarka = useRef<HTMLDivElement>(null);
  const [ile, ustawIle] = useState(widoczne.length);
  const klucz = widoczne.map((w) => w.id).join(',');

  useLayoutEffect(() => {
    const el = rzad.current;
    const m = miarka.current;
    if (!el || !m) return;
    const przelicz = () => {
      // Bez układu (jsdom, ukryta karta) nie ma czego mierzyć — zostają wszystkie.
      if (el.clientWidth === 0) return;
      const dzieci = [...m.children] as HTMLElement[];
      const szerokosci = dzieci.slice(0, -1).map((d) => d.offsetWidth);
      const plus = dzieci[dzieci.length - 1]?.offsetWidth ?? 0;
      ustawIle(ileSieMiesci(szerokosci, lacznie, plus, el.clientWidth, 4));
    };
    przelicz();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(przelicz);
    ro.observe(el);
    return () => ro.disconnect();
  }, [klucz, lacznie]);

  if (!widoczne.length) return null;
  const pastylka = (w: Wyroznik) => (
    <span
      key={w.id}
      data-wyroznik={w.id}
      className={cn(KLASA_PASTYLKI, w.pasuje ? 'bg-success-soft text-success-soft-foreground' : 'bg-muted text-foreground')}
      title={w.pasuje ? t('offer.wyrozniki.pasuje') : undefined}
    >
      {w.pasuje && <Check className="h-3 w-3 shrink-0" aria-hidden />}
      <span>{t(`offer.wyrozniki.etykieta.${w.id}`)}</span>
    </span>
  );
  const pokazane = widoczne.slice(0, ile);
  // Za „+N”: niezmieszczone z dwóch widocznych, potem reszta listy.
  const zaPlusem = [...widoczne.slice(ile), ...schowane];
  return (
    // ZAWSZE JEDNA LINIA (Piotr 30.09: wyróżniki nie mogą zjadać karty). Bez `overflow-hidden`:
    // pomiar nie dopuszcza przepełnienia, a ucięcie zabrałoby „+N” pole dotyku 44 px.
    <div ref={rzad} className="relative mt-1.5 flex min-w-0 flex-nowrap gap-1" data-pastylki-wyroznikow>
      {/* Miarka: te same pastylki w naturalnej szerokości, niewidoczne i poza przepływem. */}
      <div ref={miarka} aria-hidden className="pointer-events-none invisible absolute left-0 top-0 flex gap-1">
        {widoczne.map((w) => (
          <span key={w.id} className={cn(KLASA_PASTYLKI, 'min-w-max')}>
            {w.pasuje && <Check className="h-3 w-3 shrink-0" />}
            <span>{t(`offer.wyrozniki.etykieta.${w.id}`)}</span>
          </span>
        ))}
        <span className={KLASA_PLUSA}>{t('offer.wyrozniki.wiecej', { count: lacznie })}</span>
      </div>
      {pokazane.map(pastylka)}
      <PlusWyroznikow schowane={zaPlusem} />
    </div>
  );
}

/** (2b) Karta nad mapą: jedna pastylka + „+N” w jednym rzędzie (reguła: `pastylkaMapy`). */
export function PastylkaWyroznikaMapy({ lista }: { lista: readonly Wyroznik[] }) {
  const { t } = useTranslation();
  const { pierwsza, schowane } = pastylkaMapy(lista);
  if (!pierwsza) return null;
  // Cecha wstążkowa spoza filtrów — w kolorze wstążki, żeby lista i mapa mówiły tym samym językiem.
  const wstazkowa = !pierwsza.pasuje && KOLEJNOSC_WSTAZKI_KARTY.includes(pierwsza.id);
  return (
    // Kolumna tekstu karty ma ~148 px: „✓ Przyjazne zwierzętom” przy 10,5 px i px-2 ucinało się
    // (zrzut 30.09, 320 i pulpit). 10 px i ciaśniejsze odstępy mieszczą najdłuższą etykietę; „+N”
    // przechodzi do nowej linii, zamiast ścisnąć pastylkę.
    <div className="mt-1 flex flex-wrap items-center gap-1" data-pastylki-wyroznikow>
      <span
        data-wyroznik={pierwsza.id}
        title={pierwsza.pasuje ? t('offer.wyrozniki.pasuje') : undefined}
        className={cn(
          'inline-flex max-w-full items-center gap-0.5 whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
          pierwsza.pasuje
            ? 'bg-success-soft text-success-soft-foreground'
            : wstazkowa
              ? (TLO_WSTAZKI[pierwsza.id] ?? 'bg-muted text-foreground')
              : 'bg-muted text-foreground',
        )}
      >
        {pierwsza.pasuje && <Check className="h-3 w-3 shrink-0" aria-hidden />}
        <span>{t(`offer.wyrozniki.etykieta.${pierwsza.id}`)}</span>
      </span>
      <PlusWyroznikow schowane={schowane} className="px-1.5 text-[10px]" />
    </div>
  );
}

export interface DaneZrodla {
  dzielnica?: string | null;
  pokoje?: number | null;
  mediana?: number | null;
  ofertWPorownaniu?: number | null;
  taniejOProcent?: number | null;
  /** Zakres remontu z klasyfikacji AI (flat | kitchen | bathroom | …); `null` = brak. */
  remontZakres?: string | null;
  /** Fraza czasu remontu z opisu; do dymka idzie z niej tylko rok. */
  remontKiedy?: string | null;
  postedAt?: string | null;
  metroM?: number | null;
  /** Co właściciel PODAŁ o remoncie — z `szczegolRemontuWlasciciela`; `null` = nie podał. */
  remontWlasciciela?: SzczegolRemontuWlasciciela | null;
}

function useZrodlo() {
  const { t, i18n } = useTranslation();
  return (w: Wyroznik, d: DaneZrodla): string => {
    switch (w.id) {
      case 'zweryfikowany':
        return t('offer.wyrozniki.zrodlo.zweryfikowany');
      case 'wlasciciel':
        return t('offer.wyrozniki.zrodlo.wlasciciel');
      case 'zwierzeta':
        // Piotr 30.09: samo „Według ogłoszenia.” nie mówi, CO z tego wynika. „Wystawca”, nie
        // „właściciel” — ogłoszenie z portalu mógł dać pośrednik.
        return t('offer.wyrozniki.zrodlo.zwierzeta');
      case 'tanio': {
        // Krótko i po ludzku (Piotr 30.09: „o 24% niż typowe mieszkanie 2 pokoje, Bemowo”); liczby
        // porównania (mediana, N ogłoszeń) i zastrzeżenie „ceny ogłoszeń, nie umów” — pod ⓘ.
        if (d.taniejOProcent == null) return '';
        const typowe =
          d.pokoje == null
            ? t('offer.wyrozniki.typowe.mieszkanie')
            : d.pokoje <= 1
              ? t('offer.wyrozniki.typowe.kawalerka')
              : d.pokoje >= 4
                ? t('offer.wyrozniki.typowe.pokoje_4_plus')
                : t('offer.wyrozniki.typowe.pokoje', { liczba: d.pokoje });
        return t('offer.wyrozniki.zrodlo.tanio', { proc: d.taniejOProcent, typowe, dzielnica: d.dzielnica ?? '' }).replace(/, $/, '');
      }
      case 'remont_wlasciciela': {
        // Mówi, CO właściciel podał — nie „wykryte w opisie”. Bez roku kalendarzowego:
        // właściciel wybiera PRZEDZIAŁ („do 3 lat”), nie datę, więc „odnowione w 2025” byłoby
        // liczbą, której nie zbieramy.
        const r = d.remontWlasciciela;
        if (!r) return '';
        const co = r.kuchnia && r.lazienka
          ? t('offer.wyrozniki.remont_wl.kuchnia_i_lazienka')
          : r.kuchnia
            ? t('offer.wyrozniki.remont_wl.kuchnia')
            : t('offer.wyrozniki.remont_wl.lazienka');
        const zdanie = t('offer.wyrozniki.zrodlo.remont_wlasciciela', { co });
        return r.malowanie ? `${zdanie} ${t('offer.wyrozniki.remont_wl.malowanie')}` : zdanie;
      }
      case 'remont': {
        // Zakres z klasyfikacji AI (30.09): „Z opisu ogłoszenia: kuchnia po remoncie (2025).”
        // Z frazy czasu bierzemy tylko ROK — sama fraza jest po polsku („świeżo”, „2 lata
        // temu”) i w dymku po angielsku czy ukraińsku byłaby wtrętem. Bez zakresu (oferta
        // jeszcze nieklasyfikowana) — dotychczasowy tekst ogólny.
        const zakres = d.remontZakres;
        if (zakres !== 'flat' && zakres !== 'kitchen' && zakres !== 'bathroom') {
          return t('offer.wyrozniki.zrodlo.remont');
        }
        const rok = rokRemontu(d.remontKiedy);
        return rok
          ? t(`offer.wyrozniki.zrodlo.remont_zakres_rok.${zakres}`, { rok })
          : t(`offer.wyrozniki.zrodlo.remont_zakres.${zakres}`);
      }
      case 'nowe':
        return d.postedAt
          ? t('offer.wyrozniki.zrodlo.nowe', { count: Math.max(1, Math.round((Date.now() - new Date(d.postedAt).getTime()) / 3600e3)) })
          : '';
      case 'metro':
        return d.metroM != null ? t('offer.wyrozniki.zrodlo.metro', { m: formatLiczby(d.metroM, i18n.language) }) : '';
      default:
        return t('offer.wyrozniki.zrodlo.ogloszenie');
    }
  };
}

/**
 * (3) Strona oferty: JEDEN RZĄD MAŁYCH PASTYLEK (Piotr 30.09: „zakładałem, że to będą małe
 * wyróżniki z tooltipem, który pokazuje więcej” — duże karty zajmowały 266–282 px na telefonie).
 * Pastylka = ikona + krótka etykieta; cecha z wyszukiwania ma ✓ i zielone tło. Dotknięcie (albo
 * najechanie myszą) otwiera dymek z tym, co było w karcie: skąd to wiemy, a przy „taniej” zdanie
 * porównania i uwagę o cenach ofertowych. Telefon: jeden przewijany rząd z gradientem, jak
 * pastylki okolicy; pulpit: zawijany.
 */
export function PastylkiWyroznikowOferty({ lista, dane }: { lista: readonly Wyroznik[]; dane: DaneZrodla }) {
  if (!lista.length) return null;
  return (
    <div className="relative" data-wyrozniki-oferty>
      <div className="pointer-events-none absolute inset-y-0 -right-4 z-10 w-10 bg-gradient-to-l from-background md:hidden" aria-hidden />
      <div className="-mx-4 flex gap-1.5 overflow-x-auto px-4 pb-1 pr-10 [scrollbar-width:none] md:mx-0 md:flex-wrap md:overflow-visible md:px-0 md:pr-0 [&::-webkit-scrollbar]:hidden">
        {lista.map((w) => (
          <PastylkaZDymkiem key={w.id} w={w} dane={dane} />
        ))}
      </div>
    </div>
  );
}

function PastylkaZDymkiem({ w, dane }: { w: Wyroznik; dane: DaneZrodla }) {
  const { t } = useTranslation();
  const zrodlo = useZrodlo();
  // Najechanie / klik / dotknięcie: wspólny `DymekNajazdu` (ten sam co przy szacunku kosztu na karcie).
  const tekst = zrodlo(w, dane);
  return (
    <DymekNajazdu
      className="w-72 max-w-[calc(100vw-2rem)] space-y-1 p-3 text-sm"
      data-dymek-wyroznika={w.id}
      wyzwalacz={
        <button
          type="button"
          data-wyroznik={w.id}
          className={cn(
            'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1.5 text-[13px] font-medium transition-colors',
            'outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
            w.pasuje ? 'border-transparent bg-success-soft text-success-soft-foreground' : 'bg-card text-foreground hover:bg-muted',
          )}
        >
          {w.pasuje ? <Check className="h-3.5 w-3.5" aria-hidden /> : IKONA_PASTYLKI[w.id]}
          {t(`offer.wyrozniki.etykieta.${w.id}`)}
        </button>
      }
    >
      <p className="font-semibold">{t(`offer.wyrozniki.etykieta.${w.id}`)}</p>
      {w.pasuje && (
        <p className="inline-flex items-center gap-1 text-xs font-medium text-success">
          <Check className="h-3 w-3" aria-hidden />
          {t('offer.wyrozniki.pasuje')}
        </p>
      )}
      {tekst && <p className="text-muted-foreground">{tekst}</p>}
      {w.id === 'tanio' && dane.taniejOProcent != null && (
        <p className="text-xs text-muted-foreground">{t('offer.wyrozniki.zrodlo.tanio_info')}</p>
      )}
    </DymekNajazdu>
  );
}
